// packages/core/src/learning/schools/credit-optimization.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored to match boglehead-indexing.ts (the reference exhibit).
//
// Voice reminder: neutral enough to be trustworthy, opinionated enough to be
// memorable. Frame beliefs as "[School] believes…". Never invent quotes,
// never tell users what to buy, never copy book/creator text.
//
// ID-bound fields (deferred to the post-authoring cross-reference pass):
//   - figures:            [] (none for this school)
//   - featuredLessonIds:  [] until referenced lessons exist
//   - debateIds:          [] until referenced debates exist
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const CREDIT_OPTIMIZATION_EXHIBIT: SchoolExhibitEntry = {
	id: 'credit-optimization',
	tagline:
		'Credit optimization believes credit is useful, but never harmless.',
	worldviewSummary:
		'Credit optimization is paying attention to how lenders, scores, interest, limits, payment history, and utilization shape your financial options. This lens protects you from treating credit cards like free money or treating your score like a mystery number you cannot influence. It accepts a real tradeoff: using credit well can open doors, but it also requires attention, restraint, and clean habits. The blind spot is that score chasing can become a game where rewards, limits, and points distract from the bigger question: are you actually safer and freer?',
	coreBeliefs: [
		'Credit optimization believes credit is a system you can understand.',
		'Credit optimization believes payment history matters more than clever tricks.',
		'Credit optimization believes a useful credit score should serve your life, not run your life.',
		'Credit optimization believes credit card rewards are not wins if interest, fees, or overspending eat them.',
		'Credit optimization believes borrowing power can be helpful, but it can also hide risk.',
		'Credit optimization believes clean habits beat hacks.',
	],
	warnsAgainst: [
		'Treating available credit like available money.',
		'Chasing points while carrying expensive balances.',
		'Ignoring payment dates, utilization, fees, and interest because the monthly payment feels manageable.',
		'Believing credit optimization means taking on more debt.',
	],
	helps: 'This lens helps adults who want to understand credit scores, credit cards, borrowing power, and debt risk without fear or confusion. It may not fit when someone turns credit into a points game before their cash flow and habits are stable.',
	figures: [],
	canon: [
		{
			title: 'Payment history first',
			oneLineIdea:
				'The boring habit of paying on time matters more than most credit tricks.',
		},
		{
			title: 'Utilization awareness',
			oneLineIdea:
				'How much credit you use compared with your limits can affect how lenders read your risk.',
		},
		{
			title: 'Interest beats rewards',
			oneLineIdea:
				'A reward is not a reward if carrying the balance costs more than the benefit.',
		},
		{
			title: 'Credit as access',
			oneLineIdea:
				'Credit can affect apartments, cars, loans, and options, which is why the system deserves attention.',
		},
		{
			title: 'Borrowing power is not safety',
			oneLineIdea:
				'A higher limit gives flexibility, but it does not replace savings, income, or judgment.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-credit-optimization',
	debateIds: [],
	applyActions: [
		{
			label: 'Look at your current money picture before using credit',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test how a monthly payment changes your future options',
			action: {
				type: 'openSimulator',
				label: 'Simulate the payment',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check how much runway protects you from relying on credit',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Write your credit rules before rewards tempt you',
			action: {
				type: 'noToolAction',
				label: 'Write your credit rules',
				prompt: 'Write three rules for when you will use credit, when you will pause, and what balance you refuse to carry.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Credit is not free money. It is borrowed flexibility with rules attached.',
			kind: 'hook',
		},
		{
			line: 'Points feel smart until interest turns the game against you.',
			kind: 'hook',
		},
		{
			line: 'What credit mistake taught you that the minimum payment is not the real cost?',
			kind: 'interview-question',
		},
		{
			line: 'When did your credit score actually change your options in real life?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches credit without fear, hype, or points worship.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'A better score should mean better options, not a more expensive lifestyle.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience how credit can help access while still creating real downside.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people whether credit made them more free, more stressed, or both.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
